import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { publicAssetUrl } from "@/lib/api";
import { listPartnerBrands, partnerCtaLabel, trackPartnerClick } from "@/lib/partners";
import { ExternalLink } from "lucide-react";

/**
 * Renders a prominent "Shop on X"/"Book on X"/… CTA that fires the
 * PARTNER_LINK track event and opens the partner URL in a new tab.
 *
 * Also shows a small "Partner: X" badge (optional) alongside.
 */
export const PartnerCTA = ({ post, className = "" }) => {
    const [brand, setBrand] = useState(null);
    const partnerUrl = post?.details?.partner_url;
    const brandSlug = post?.details?.partner_brand_slug;

    useEffect(() => {
        if (!brandSlug) return;
        listPartnerBrands()
            .then((rows) => setBrand((rows || []).find((b) => b.slug === brandSlug) || null))
            .catch(() => setBrand(null));
    }, [brandSlug]);

    if (!partnerUrl) return null;

    const label = partnerCtaLabel(post, brand);

    const onClick = async (e) => {
        e.preventDefault();
        // Fire-and-forget the tracking call BEFORE opening the tab.
        trackPartnerClick(post.id);
        // Give the request a moment to start; some browsers block
        // new-tab open after long awaits, so we don't await the promise.
        window.open(partnerUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <div className={className} data-testid="partner-cta-wrap">
            <Button
                onClick={onClick}
                className="h-12 w-full nest-gradient text-base font-semibold text-white"
                data-testid="partner-cta-btn"
            >
                {brand?.logo_url && (
                    <img
                        src={publicAssetUrl(brand.logo_url)}
                        alt=""
                        className="mr-2 h-6 w-6 rounded bg-white/90 p-0.5"
                    />
                )}
                <span>{label}</span>
                <ExternalLink size={14} className="ml-2 opacity-90" />
            </Button>
            {brand?.name && (
                <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-700" data-testid="partner-brand-badge">
                    Partner: {brand.name}
                </div>
            )}
        </div>
    );
};

export default PartnerCTA;
